/**
 * Role Context Provider
 *
 * Provides role-based access control throughout the application.
 * Integrates with the CBO roles system to provide:
 * - Current user role information
 * - Permission checking utilities
 * - Navigation filtering
 * - UI simplification preferences
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  CBORoleType,
  Permission,
  RoleDefinition,
  NavItem,
  ROLE_DEFINITIONS,
  roleHasPermission,
  roleHasAnyPermission,
  roleHasAllPermissions,
  getNavigationForRole,
  getDefaultDashboardForRole,
  shouldUseSimplifiedUI,
  shouldShowAdvancedFeatures,
  getManageableRoles,
  mapLegacyRole,
} from '@/types/cboRoles';
import { config as productConfig } from '@/src/config';

// Get the backend API URL - on dev.weavein.ai use relative URLs via Vercel proxy (avoids CORS).
// productConfig.weaveinApi.baseUrl is product-aware (Triveny → ms-api-hots,
// WeaveIn → weavein-api); direct VITE_WEAVEIN_API_URL reads hit the wrong
// backend on Triveny and 401 every platform JWT.
const API_BASE_URL = (typeof window !== 'undefined' && window.location.hostname === 'dev.weavein.ai')
  ? ''
  : (productConfig.weaveinApi?.baseUrl ?? '').trim();

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface UserRoleInfo {
  userId: number;
  organizationId: number;
  role: CBORoleType;
  roleDefinition: RoleDefinition;
  assignedMemberIds?: number[];
  assignedProgramIds?: number[];
}

interface RoleContextValue {
  // Current user role info
  currentRole: CBORoleType | null;
  userRoleInfo: UserRoleInfo | null;
  roleDefinition: RoleDefinition | null;
  isLoading: boolean;
  error: string | null;

  // Permission checks
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  canAccessPath: (path: string) => boolean;

  // Navigation
  navigationItems: NavItem[];
  defaultDashboard: string;

  // UI preferences
  useSimplifiedUI: boolean;
  showAdvancedFeatures: boolean;

  // Role management
  manageableRoles: CBORoleType[];
  canManageRole: (targetRole: CBORoleType) => boolean;

  // Actions
  refreshRole: () => Promise<void>;
  setMockRole: (role: CBORoleType) => void; // For testing/demos
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultContext: RoleContextValue = {
  currentRole: null,
  userRoleInfo: null,
  roleDefinition: null,
  isLoading: true,
  error: null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  canAccessPath: () => false,
  navigationItems: [],
  defaultDashboard: '/dashboard',
  useSimplifiedUI: false,
  showAdvancedFeatures: false,
  manageableRoles: [],
  canManageRole: () => false,
  refreshRole: async () => {},
  setMockRole: () => {},
};

// ============================================================================
// CONTEXT
// ============================================================================

const RoleContext = createContext<RoleContextValue>(defaultContext);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface RoleProviderProps {
  children: ReactNode;
  organizationId?: number;
  userId?: number;
  initialRole?: CBORoleType;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function RoleProvider({
  children,
  organizationId,
  userId,
  initialRole,
}: RoleProviderProps) {
  const [currentRole, setCurrentRole] = useState<CBORoleType | null>(initialRole ?? null);
  const [userRoleInfo, setUserRoleInfo] = useState<UserRoleInfo | null>(null);
  const [isLoading, setIsLoading] = useState(!initialRole);
  const [error, setError] = useState<string | null>(null);
  const [isMockRole, setIsMockRole] = useState(false);

  /**
   * Fetch user role from API
   */
  const fetchUserRole = useCallback(async () => {
    if (isMockRole) return;

    const effectiveOrgId = organizationId ?? parseInt(localStorage.getItem('active_cbo_id') ?? '0', 10);
    const effectiveUserId = userId ?? parseInt(localStorage.getItem('user_id') ?? '0', 10);

    if (!effectiveOrgId || !effectiveUserId) {
      setIsLoading(false);
      setCurrentRole(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const roleUrl = `${API_BASE_URL}/api/cbos/${effectiveOrgId}/users/${effectiveUserId}/role`;

      // No token preview. Even a 20-character prefix is credential material,
      // and console output can be forwarded to RUM/log collectors.
      console.debug('🎭 RoleContext - Fetching user role:', {
        url: roleUrl,
        hasToken: !!token,
        effectiveOrgId,
        effectiveUserId
      });

      const response = await fetch(
        roleUrl,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': effectiveOrgId.toString(),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // A 404 here is EXPECTED, not a failure. The endpoint resolves a role
        // from user_organizations, and platform administrators legitimately
        // have no row there — user 1 has zero, verified against production —
        // so it 404s on every load and the legacy fallback below handles it.
        // Logging that at error level made a working path look broken and
        // buried real failures (401/403/500) in the same noise.
        const expected = response.status === 404;
        const detail = { url: roleUrl, status: response.status, statusText: response.statusText };
        if (expected) {
          console.debug('🎭 RoleContext - no org-scoped role row; using the legacy path', detail);
        } else {
          console.error('🎭 RoleContext - Role endpoint failed:', detail);
        }

        // Fall back to legacy role from user organizations
        const legacyUrl = `${API_BASE_URL}/api/cbos/users/me/organizations`;
        console.debug('🎭 RoleContext - Falling back to legacy endpoint:', { url: legacyUrl });

        const legacyResponse = await fetch(legacyUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': effectiveOrgId.toString(),
            'Content-Type': 'application/json',
          },
        });

        if (legacyResponse.ok) {
          const orgs = await legacyResponse.json();
          const currentOrg = orgs.find((o: any) => o.cbo_id === effectiveOrgId);
          if (currentOrg) {
            const mappedRole = mapLegacyRole(currentOrg.role);
            console.log('🎭 RoleContext - Legacy role fetched successfully:', {
              role: mappedRole,
              organizationId: effectiveOrgId
            });
            setCurrentRole(mappedRole);
            setUserRoleInfo({
              userId: effectiveUserId,
              organizationId: effectiveOrgId,
              role: mappedRole,
              roleDefinition: ROLE_DEFINITIONS[mappedRole],
            });
          }
        } else {
          console.error('🎭 RoleContext - Legacy endpoint also failed:', {
            url: legacyUrl,
            status: legacyResponse.status,
            statusText: legacyResponse.statusText,
            hasToken: !!token
          });
          throw new Error('Failed to fetch user role');
        }
      } else {
        const data = await response.json();
        const role = data.role as CBORoleType;
        setCurrentRole(role);
        setUserRoleInfo({
          userId: effectiveUserId,
          organizationId: effectiveOrgId,
          role,
          roleDefinition: ROLE_DEFINITIONS[role],
          assignedMemberIds: data.assigned_member_ids,
          assignedProgramIds: data.assigned_program_ids,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      // Default to staff role on error
      setCurrentRole('staff');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, userId, isMockRole]);

  /**
   * Refresh role information
   */
  const refreshRole = useCallback(async () => {
    await fetchUserRole();
  }, [fetchUserRole]);

  /**
   * Set mock role for testing/demos
   */
  const setMockRole = useCallback((role: CBORoleType) => {
    setIsMockRole(true);
    setCurrentRole(role);
    setUserRoleInfo({
      userId: 0,
      organizationId: 0,
      role,
      roleDefinition: ROLE_DEFINITIONS[role],
    });
    setIsLoading(false);
    setError(null);
  }, []);

  // Consolidated useEffect: Load role on mount and listen for auth/org changes
  // Combines storage, auth, and organization event listeners for better performance
  useEffect(() => {
    // Skip if using mock role or initial role is provided
    if (initialRole || isMockRole) {
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      // Token exists, fetch role immediately
      fetchUserRole();
    } else {
      setIsLoading(false);
    }

    // Handle storage events (cross-tab auth/org changes)
    const handleStorageChange = (e: StorageEvent) => {
      if (isMockRole) return;

      if (e.key === 'auth_token') {
        if (e.newValue) {
          fetchUserRole();
        } else {
          // Token removed (logout)
          setCurrentRole(null);
          setUserRoleInfo(null);
          setIsLoading(false);
        }
      } else if (e.key === 'active_cbo_id' && e.newValue) {
        fetchUserRole();
      }
    };

    // Handle auth state changes within the same tab
    const handleAuthChange = () => {
      if (isMockRole) return;
      const newToken = localStorage.getItem('auth_token');
      if (newToken) {
        fetchUserRole();
      } else {
        setCurrentRole(null);
        setUserRoleInfo(null);
        setIsLoading(false);
      }
    };

    // Handle organization changes within the same tab
    const handleOrgChange = () => {
      if (isMockRole) return;
      fetchUserRole();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-state-changed', handleAuthChange);
    window.addEventListener('authStateChanged', handleAuthChange);
    window.addEventListener('organizationChanged', handleOrgChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-state-changed', handleAuthChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('organizationChanged', handleOrgChange);
    };
  }, [fetchUserRole, initialRole, isMockRole]);

  // Computed values
  const roleDefinition = currentRole ? ROLE_DEFINITIONS[currentRole] : null;
  const navigationItems = currentRole ? getNavigationForRole(currentRole) : [];
  const defaultDashboard = currentRole ? getDefaultDashboardForRole(currentRole) : '/dashboard';
  const useSimplifiedUI = currentRole ? shouldUseSimplifiedUI(currentRole) : false;
  const showAdvancedFeatures = currentRole ? shouldShowAdvancedFeatures(currentRole) : false;
  const manageableRoles = currentRole ? getManageableRoles(currentRole) : [];

  // Permission checking functions
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!currentRole) return false;
      return roleHasPermission(currentRole, permission);
    },
    [currentRole]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      if (!currentRole) return false;
      return roleHasAnyPermission(currentRole, permissions);
    },
    [currentRole]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      if (!currentRole) return false;
      return roleHasAllPermissions(currentRole, permissions);
    },
    [currentRole]
  );

  const canAccessPath = useCallback(
    (path: string): boolean => {
      // Find nav item for path and check permissions
      const findNavItem = (items: NavItem[], targetPath: string): NavItem | undefined => {
        for (const item of items) {
          if (item.path === targetPath) return item;
          if (item.children) {
            const found = findNavItem(item.children, targetPath);
            if (found) return found;
          }
        }
        return undefined;
      };

      const navItem = findNavItem(navigationItems, path);
      if (!navItem) return true; // Allow access to paths not in navigation
      if (navItem.requiredPermissions.length === 0) return true;
      return hasAnyPermission(navItem.requiredPermissions);
    },
    [navigationItems, hasAnyPermission]
  );

  const canManageRole = useCallback(
    (targetRole: CBORoleType): boolean => {
      return manageableRoles.includes(targetRole);
    },
    [manageableRoles]
  );

  const contextValue: RoleContextValue = {
    currentRole,
    userRoleInfo,
    roleDefinition,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessPath,
    navigationItems,
    defaultDashboard,
    useSimplifiedUI,
    showAdvancedFeatures,
    manageableRoles,
    canManageRole,
    refreshRole,
    setMockRole,
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Access role context
 */
export function useRole(): RoleContextValue {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

/**
 * Check if user has a specific permission
 */
export function usePermission(permission: Permission): boolean {
  const { hasPermission, isLoading } = useRole();
  return !isLoading && hasPermission(permission);
}

/**
 * Check if user has any of the specified permissions
 */
export function useAnyPermission(permissions: Permission[]): boolean {
  const { hasAnyPermission, isLoading } = useRole();
  return !isLoading && hasAnyPermission(permissions);
}

/**
 * Check if user has all of the specified permissions
 */
export function useAllPermissions(permissions: Permission[]): boolean {
  const { hasAllPermissions, isLoading } = useRole();
  return !isLoading && hasAllPermissions(permissions);
}

/**
 * Get current user's navigation items
 */
export function useNavigation(): { items: NavItem[]; isLoading: boolean } {
  const { navigationItems, isLoading } = useRole();
  return { items: navigationItems, isLoading };
}

/**
 * Check if current role should use simplified UI
 */
export function useSimplifiedUI(): boolean {
  const { useSimplifiedUI } = useRole();
  return useSimplifiedUI;
}

// ============================================================================
// GUARD COMPONENTS
// ============================================================================

interface PermissionGuardProps {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Render children only if user has the required permission
 */
export function PermissionGuard({
  permission,
  children,
  fallback = null,
}: PermissionGuardProps): React.ReactElement | null {
  const hasPermission = usePermission(permission);
  return hasPermission ? <>{children}</> : <>{fallback}</>;
}

interface AnyPermissionGuardProps {
  permissions: Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Render children only if user has any of the required permissions
 */
export function AnyPermissionGuard({
  permissions,
  children,
  fallback = null,
}: AnyPermissionGuardProps): React.ReactElement | null {
  const hasAny = useAnyPermission(permissions);
  return hasAny ? <>{children}</> : <>{fallback}</>;
}

interface RoleGuardProps {
  roles: CBORoleType[];
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Render children only if user has one of the specified roles
 */
export function RoleGuard({
  roles,
  children,
  fallback = null,
}: RoleGuardProps): React.ReactElement | null {
  const { currentRole, isLoading } = useRole();
  if (isLoading) return null;
  const hasRole = currentRole && roles.includes(currentRole);
  return hasRole ? <>{children}</> : <>{fallback}</>;
}

export default RoleContext;
